import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["alt", "className", "handleError", "handleLoad", "height", "resource", "type", "width", "widths", "ratio"];
import React from "react";
import { func, instanceOf, number, oneOfType, string } from "prop-types";
import { useResourceImage } from "../../../peregrine/talons/Image/useResourceImage";
import { generateSrcset, generateUrl } from "../../util/images";
import { cdnImageUrl } from "@Src_main/utils/generalUtils";

/**
 * Renders a Magento resource image.
 *
 * @param {string}   props.alt the alt attribute to apply to the image.
 * @param {string}   props.className the class to apply to this image.
 * @param {Func}     props.handleError the function to call if the image fails to load.
 * @param {Func}     props.handleLoad the function to call if the image successfully loads.
 * @param {number}   props.height the height to request for the fallback image for browsers that don't support srcset / sizes.
 * @param {string}   props.resource the Magento path to the image ex: /v/d/vd12-rn_main_2.jpg
 * @param {string}   props.type the Magento image type ("image-category" / "image-product"). Used to build the resource URL.
 * @param {number}   props.width the intrinsic width of the image & the width to request for the fallback image for browsers that don't support srcset / sizes.
 * @param {Map}      props.widths a map of breakpoints to possible widths used to create the img's sizes attribute.
 * @param {number}   props.ratio is the image width to height ratio. Defaults to 4/5.
 */
var ResourceImage = props => {
  var {
      alt,
      className,
      handleError,
      handleLoad,
      height,
      resource,
      type,
      width,
      widths,
      ratio
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  var talonProps = useResourceImage({
    generateSrcset,
    generateUrl,
    height,
    resource,
    type,
    width,
    widths,
    ratio
  });
  var {
    sizes,
    src,
    srcSet
  } = talonProps;

  // Note: Attributes that are allowed to be overridden must appear before the spread of `rest`.
  return /*#__PURE__*/React.createElement("img", _extends({
    loading: "lazy"
  }, rest, {
    alt: alt,
    className: className,
    onError: handleError,
    onLoad: handleLoad,
    sizes: sizes,
    src: cdnImageUrl(src),
    srcSet: cdnImageUrl(src),
    width: width
  }));
};
ResourceImage.propTypes = {
  alt: string.isRequired,
  className: string,
  handleError: func,
  handleLoad: func,
  resource: string.isRequired,
  height: oneOfType([number, string]),
  type: string,
  width: oneOfType([number, string]),
  widths: instanceOf(Map)
};
ResourceImage.defaultProps = {
  type: "image-product"
};
export default ResourceImage;